import { useEffect, useMemo, useState } from 'react';
import { Client, type CaseProfile } from './api';

type Props = {
  client: Client;
  notify: (message: string) => void;
  onCreated: (caseId: string) => Promise<void> | void;
};

const text = (value: unknown) => String(value ?? '—');

export default function CaseProfileCatalog({ client, notify, onCreated }: Props) {
  const [profiles, setProfiles] = useState<CaseProfile[]>([]);
  const [capabilities, setCapabilities] = useState<Array<Record<string, unknown>>>([]);
  const [cutoffs, setCutoffs] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState('');

  useEffect(() => {
    void client.coldCaseProfiles().then((result) => {
      setProfiles(result.profiles);
      setCapabilities(result.capabilities);
    }).catch((error) => notify(error instanceof Error ? error.message : 'Falha ao carregar perfis Cold Case'))
      .finally(() => setLoading(false));
  }, [client, notify]);

  const capsByProfile = useMemo(() => {
    const map = new Map<string, Array<Record<string, unknown>>>();
    for (const item of capabilities) {
      const key = text(item.case_code);
      map.set(key, [...(map.get(key) ?? []), item]);
    }
    return map;
  }, [capabilities]);

  if (loading) return <section className="panel"><p className="empty">Carregando perfis de benchmark…</p></section>;

  return <section className="panel profile-catalog">
    <div className="section-heading">
      <div><p className="eyebrow">Benchmark cego configurável</p><h2>Perfis CASE-001 a CASE-006</h2></div>
      <span>{profiles.length} perfis</span>
    </div>
    <p className="profile-policy">Os perfis contêm apenas capacidades e regras de execução. Narrativas, desfechos e fontes da internet não são pré-carregados. A instância exige data de corte e o conjunto de fontes deve ser congelado antes da consulta ao desfecho.</p>
    <div className="profile-grid">{profiles.map((profile) => {
      const items = capsByProfile.get(profile.case_code) ?? [];
      const cutoff = cutoffs[profile.case_code] ?? '';
      return <article className="profile-card" key={profile.case_code}>
        <div className="cc-row"><span className="pill">{profile.case_code}</span><span className="cc-status ok">{profile.status}</span></div>
        <h3>{profile.display_name}</h3>
        <p>{profile.primary_capacity}</p>
        <div className="profile-capabilities">{items.map((item) => <span key={text(item.capability_code)}>{text(item.capability_code).replaceAll('_',' ')}</span>)}</div>
        <ul className="cc-bullets compact">
          <li>Fontes: {profile.source_policy.replaceAll('_',' ')}</li>
          <li>Data de corte obrigatória</li>
          <li>Previsões congeladas antes do desfecho</li>
          <li>Narrativa pré-carregada: não</li>
        </ul>
        <label>Data de corte da instância<input type="datetime-local" value={cutoff} onChange={(e) => setCutoffs((current) => ({ ...current, [profile.case_code]: e.target.value }))}/></label>
        <button className="primary" disabled={!cutoff || creating === profile.case_code} onClick={async () => {
          if (!cutoff) return;
          setCreating(profile.case_code);
          try {
            const result = await client.instantiateCaseProfile(profile.case_code, { cutoffAt: new Date(cutoff).toISOString() });
            notify(`${profile.case_code} criado sem narrativa ou desfecho pré-carregado.`);
            await onCreated(result.caseId);
          } catch (error) {
            notify(error instanceof Error ? error.message : 'Falha ao instanciar perfil');
          } finally { setCreating(''); }
        }}>{creating === profile.case_code ? 'Criando…' : 'Criar instância cega'}</button>
      </article>;
    })}</div>
  </section>;
}
